import squel from 'squel/dist/squel-basic.min';
import {sortBy, remove, find, result, omit} from 'lodash';
import DATASOURCES from '../../../../services/datasources';
import PIPELINE_STEPS from '../../../../utils/pipeline_steps';
import ColumnModel from '../../../datasource/column';
import TableModel from '../../../datasource/table';
import {ATTRIBUTES} from "../../../../utils/query_utils";
import PipelineStepModel from '../pipeline_step_model';
import DimensionModel from './dimension';

export default class DatasetModel extends PipelineStepModel {
    type = PIPELINE_STEPS.DATASET;
    datasource = '';
    measures = [];
    dimensions = [];
    filters = [];
    columns = [];
    rowLimit = null;
    _sql = null;
    _sampleTable = null;

    constructor({datasource, measures = [], dimensions = [], filters = [], columns = [], rowLimit, ...otherProps} = {}) {
        super(otherProps);
        Object.assign(
            this,
            {
                datasource,
                measures,
                dimensions,
                filters,
                columns,
                rowLimit,
            },
        );
    }

    addOrUpdateAttribute(attribute) {
        const columnsIndex = this.columns.findIndex(({id}) => attribute.id === id);
        const oldAttribute = this.columns[columnsIndex];
        const attributes = this.getAttributesOfType(attribute.type);
        if (oldAttribute && oldAttribute.type !== attribute.type) {
            const oldAttributes = this.getAttributesOfType(oldAttribute.type);
            const oldIndex = oldAttributes.findIndex(({id}) => attribute.id === id);
            oldAttributes.splice(oldIndex, 1);
            attributes.push(attribute);
            this.columns[columnsIndex] = attribute;
        } else {
            const index = attributes.findIndex(({id}) => attribute.id === id);
            if (index >= 0) {
                attributes[index] = attribute;
                if (attribute.type !== ATTRIBUTES.FILTER) {
                    this.columns[columnsIndex] = attribute;
                }
            } else {
                attributes.push(attribute);
                if (attribute.type !== ATTRIBUTES.FILTER) {
                    this.columns.push(attribute);
                }
            }
        }
        this._resetQueryResults();
    }

    _resetQueryResults() {
        this._sql = null;
        this._sampleTable = null;
    }

    removeAttribute(attribute) {
        const attributes = this.getAttributesOfType(attribute.type);
        remove(attributes, {id: attribute.id});

        if (attribute.type !== ATTRIBUTES.FILTER) {
            remove(this.columns, {id: attribute.id});
        }
        this._resetQueryResults()
    }

    changeDatasource(datasource) {
        Object.assign(this, {
            datasource,
            columns: [],
            measures: [],
            dimensions: [],
            filters: [],
        });
    }

    /**
     *
     * @param type
     * @returns Array
     */
    getAttributesOfType(type) {
        switch (type) {
            case ATTRIBUTES.MEASURE:
                return this.measures;
            case ATTRIBUTES.DIMENSION:
                return this.dimensions;
            case ATTRIBUTES.FILTER:
                return this.filters;
            case ATTRIBUTES.COLUMN:
                return this.columns;
            default:
                return;
        }
    }

    getAttributeWithId(id) {
        return find(this.columns, {id});
    }

    getQueryResults() {
        const datasource = DATASOURCES[this.datasource];
        if (datasource.canQuery() && this.sql) {
            return datasource.executeQuery(this.sql);
        }
        return null;
    }

    getSampleTable() {
        if (!this._sampleTable) {
            const sampleTable = new TableModel({name: `dataset_${this.id}_query_result`}, undefined, {sortColumns: false});
            const {result} = this.getQueryResults() || {};
            for (const attribute of this.dimensions.concat(this.measures)) {
                const column = new ColumnModel(omit(attribute.column, 'id'));
                column.title_name = attribute.label;
                column.name = attribute.id;
                if (result) {
                    column.sample = result.map(row => row[attribute.id]);
                }
                if (attribute instanceof DimensionModel) {
                    column.group = 'Group by';
                }
                sampleTable.addColumn(column);
            }
            this._sampleTable = sampleTable;
        }
        return this._sampleTable;
    }

    getExecutionTime() {
        return result(this.getQueryResults(), 'executionTime', Math.round(Math.random() * 30));
    }

    getSql(rowCount = this.rowLimit) {
        if (!this._sql) {
            const sql = squel.select({fieldAliasQuoteCharacter: '`'});
            const dimensions = this.dimensions.filter(dim => dim.isComplete());
            const measures = this.measures.filter(measure => measure.isComplete());
            const filters = this.filters.filter(filter => filter.isComplete());
            if (dimensions.length + measures.length === 0) {
                return null;
            }
            for (const attr of [].concat(dimensions, measures).filter(attr => attr.isComplete())) {
                sql.field(attr.sql, attr.id);
            }
            for (const dimension of dimensions) {
                sql.group(dimension.sql);
            }
            for (const filter of filters) {
                sql.where(filter.sql);
            }
            let tables = [];
            [].concat(dimensions, measures, filters).forEach(({column}) => {
                if (tables.findIndex(table => table.name === column.table.name) === -1) {
                    tables.push(column.table);
                }
            });
            tables = sortBy(tables, 'name');
            sql.from(tables[0].sqlName);
            const joins = DATASOURCES[this.datasource].getJoins(tables);
            if (tables.length > 1 && !joins.length) {
                return null;
            }
            for (const {from, to} of joins) {
                sql.join(to.table.sqlName, null, `${from.sqlName} = ${to.sqlName}`);
            }
            if (rowCount > 0) {
                sql.limit(rowCount);
            }
            this._sql = sql.toString();
        }
        console.debug(this._sql);
        return this._sql;
    }

    hasAttribute(attribute) {
        return this.getAttributesOfType(attribute.type).includes(attribute);
    }

    apply() {
        const table = this.getSampleTable();
        return {before: null, after: table, table};
    }

    get sql() {
        return this.getSql();
    }
}
